import React, { useState, useEffect, useCallback } from 'react';

// Rating emotion icons for experience rating
const ratingEmojis = {
  excellent: '😄',
  good: '🙂',
  okay: '😐',
  poor: '😞',
  veryPoor: '😢'
};
import RecipeDetail from '../RecipeDetail';
import './RecipeResults.css';

const PAGE_SIZE = 4;
const RECIPE_API_URL = process.env.REACT_APP_RECIPE_API_URL;
const RECIPE_API_KEY = process.env.REACT_APP_RECIPE_API_KEY;

const getRecipeErrorMessage = (status) => {
  switch (status) {
    case 400:
      return 'Oh no! This pantry seems to be empty. Try scanning again, and we’ll cooking up something great for you.';
    case 401:
      return 'Oops, we’re juggling a few too many plates right now. Could you give us another shot in a minute or two? Appreciate you hanging in there!';
    case 422:
      return 'Uh oh, one or more of these ingredients is not part of our data base. Can you please check and try again?';
    case 500:
      return 'Oops, we’re juggling a few too many plates right now. Could you give us another shot in a minute or two? Appreciate you hanging in there!';
    default:
      return null;
  }
};

function RecipeResults({ ingredients, preferences, onBackToQuestions, onBackToHome, onSelectRecipe }) {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [loadMoreCount, setLoadMoreCount] = useState(0); // Track how many times 'Load more' is pressed
  const [selectedRating, setSelectedRating] = useState(null);
  const [showRatingThanks, setShowRatingThanks] = useState(false);

  const fetchRecipes = useCallback(async ({ pageToLoad = 1, append = false } = {}) => {
    try {
      if (append) {
        setLoadMoreLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      //Remove Knorr products from ingredients by just removing the Knorr word like Knorr Ketchup becomes Knorr
      const filteredIngredients =  ingredients.map(item =>item.replace(/^Knorr\s*/i, "").trim()
  );


      const payload = {
        ingredients: filteredIngredients,
        servings: preferences.servingSize,
        time_of_day: preferences.mealTime.toLowerCase(),
        meta: {
          brand: 'knorr',
          locale: 'en-pk',
          version: 'v2'
        },
        limit: PAGE_SIZE,
        offset: (pageToLoad - 1) * PAGE_SIZE,
      };

      if (!RECIPE_API_URL || !RECIPE_API_KEY) {
        throw new Error('Missing recipe API configuration. Please set REACT_APP_RECIPE_API_URL and REACT_APP_RECIPE_API_KEY in your .env file.');
      }

      const response = await fetch(
        RECIPE_API_URL,
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': RECIPE_API_KEY
          },
          body: JSON.stringify(payload)
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const brandMessage = getRecipeErrorMessage(response.status);
        throw new Error(brandMessage || data.message || `Failed to fetch recipes: ${response.statusText}`);
      }

      const newRecipes = data.data?.recipes || [];

      setRecipes(prev => {
        if (!append) return newRecipes;
        return [...prev, ...newRecipes];
      });
      setPage(pageToLoad);
    } catch (err) {
      console.error('Error fetching recipes:', err);
      if (err.message.includes('Failed to fetch')) {
        setError('Unable to connect to the recipe API. Please check your internet connection or try again later.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setLoadMoreLoading(false);
    }
  }, [ingredients, preferences]);

  useEffect(() => {
    fetchRecipes({ pageToLoad: 1, append: false });
  }, [fetchRecipes]);

  const handleLoadMore = () => {
    if (loadMoreCount < 2) {
      fetchRecipes({ pageToLoad: page + 1, append: true });
      setLoadMoreCount(count => count + 1);
    }
  };

  const handleRating = (rating) => {
    setSelectedRating(rating);
    setShowRatingThanks(true);
    setTimeout(() => setShowRatingThanks(false), 2000);
    if (typeof window.gtag === 'function') {
    window.gtag('event', 'recipe_rating', {
      'rating_label': rating, // This will be 'veryPoor', 'okay', etc.
      'page_location': window.location.href
    });
    }
  };

  return (
    <div className="recipe-results">
      <div className="recipe-results-header">
        <button 
          className="back-to-questions-button" 
          onClick={onBackToQuestions}
          type="button"
        >
          ← Back to questions
        </button>
        {/* <h1 className="results-title">Your Personalized Recipes</h1>
        <p className="results-subtitle">
        Based on: {ingredients.join(', ')} for {preferences.servingSize} people at {preferences.mealTime}
        </p> */}
      </div>
     
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Something's Cooking...</p>
        </div>
      )}

      {error && (
        <div className="error-container">
          <p className="error-message">
            ⚠️ {error}
          </p>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && recipes.length > 0 && (
        <div className="recipes-container">
          {recipes.map((recipe, index) => (
            <RecipeDetail key={index} recipe={recipe} onSelect={onSelectRecipe} mealTime={preferences.mealTime}/>
          ))}
        </div>
      )}

      {/* Actions and notes */}
      {!loading && !error && (
        <div className="results-actions">

          <div className="results-buttons-row">
            <button
              className="primary-button"
              onClick={handleLoadMore}
              disabled={loadMoreLoading || loadMoreCount >= 2}
            >
              {loadMoreLoading ? 'Loading…' : (loadMoreCount >= 2 ? 'No more results' : 'Load more')}
            </button>
            <button
              className="secondary-button"
              onClick={onBackToHome}
              type="button"
            >
              Start over
            </button>
          </div>
          <p className="dietary-note">
            For any specific dietary concerns, always refer to product packaging and consult a diet & health expert.
          </p>

        <div className="rating-card">
          <h3 className="rating-title">Rate your experience</h3>
          <div className="rating-options-row">
            <button
              className={`rating-button ${selectedRating === 'veryPoor' ? 'active' : ''}`}
              onClick={() => handleRating('veryPoor')}
              type="button"
              title="Very poor"
            >
              <div className="rating-circle">{ratingEmojis.veryPoor}</div>
              <span className="rating-label">Not<br/>Vibing</span>
            </button>
            <button
              className={`rating-button`}
              onClick={() => handleRating('poor')}
              type="button"
              title="Poor"
            >
              <div className={`rating-circle ${selectedRating === 'poor' ? 'active' : ''}`}>{ratingEmojis.poor}</div>
              <span className="rating-label">Still<br/>Confused</span>
            </button>
            <button
              className={`rating-button`}
              onClick={() => handleRating('okay')}
              type="button"
              title="Okay"
            >
              <div className={`rating-circle ${selectedRating === 'okay' ? 'active' : ''}`}>{ratingEmojis.okay}</div>
              <span className="rating-label">Meh,<br/>IDK</span>
            </button>
            <button
              className={`rating-button`}
              onClick={() => handleRating('good')}
              type="button"
              title="Good"
            >
              <div className={`rating-circle ${selectedRating === 'good' ? 'active' : ''}`}>{ratingEmojis.good}</div>
              <span className="rating-label">Refresh-<br/>Ready</span>
            </button>
            <button
              className={`rating-button`}
              onClick={() => handleRating('excellent')}
              type="button"
              title="Excellent"
            >
              <div className={`rating-circle ${selectedRating === 'excellent' ? 'active' : ''}`}>{ratingEmojis.excellent}</div>
              <span className="rating-label">Chef<br/>Vibes</span>
            </button>
          </div>
          {showRatingThanks && (
            <p className="rating-thanks">Thanks for rating us!</p>
          )}
        </div>
        </div>
      )}


      {!loading && !error && recipes.length === 0 && (
        <div className="no-recipes-container">
          <p>No recipes found. Please try with different ingredients.</p>
        </div>
      )}
    </div>
  );
}

export default RecipeResults;
